import {
  MAX_SCALE,
  MIN_SCALE,
  panCamera,
  zoomCamera,
  type Camera,
  type Vec2,
  type Viewport,
} from "./camera";

export const MOUSE_DRAG_PX = 7;
export const TOUCH_DRAG_PX = 12;
export const NARROW_VIEWPORT_PX = 720;
export const DESKTOP_CULL_MARGIN = 240;
export const MOBILE_CULL_MARGIN = 72;
export const DESKTOP_CLAMP_PAD = 96;
export const MOBILE_CLAMP_PAD = 168;
export const HISTORY_EDGE_PX = 24;
export const SHEET_CLOSE_PX = 80;

export function isNarrowViewport(viewport: Pick<Viewport, "width">): boolean {
  return viewport.width <= NARROW_VIEWPORT_PX;
}

export function isPortraitViewport(viewport: Viewport): boolean {
  return viewport.height / viewport.width > 1.15;
}

export function dragThreshold(pointerType: string | undefined): number {
  return pointerType === "touch" || pointerType === "pen"
    ? TOUCH_DRAG_PX
    : MOUSE_DRAG_PX;
}

export function pinchOf(a: Vec2, b: Vec2): { dist: number; mid: Vec2 } {
  return {
    dist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
    mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
  };
}

export function applyPinch(
  cam: Camera,
  prev: { dist: number; mid: Vec2 },
  next: { dist: number; mid: Vec2 },
  origin: Vec2,
  minScale?: number,
  maxScale?: number,
): Camera {
  const factor = next.dist / (prev.dist || next.dist);
  const zoomed = zoomCamera(cam, factor, origin.x, origin.y, minScale, maxScale);
  return panCamera(zoomed, next.mid.x - prev.mid.x, next.mid.y - prev.mid.y);
}

export function viewCullMargin(viewport: Pick<Viewport, "width">): number {
  return isNarrowViewport(viewport) ? MOBILE_CULL_MARGIN : DESKTOP_CULL_MARGIN;
}

export function clampPad(viewport: Viewport): number {
  return isNarrowViewport(viewport) || isPortraitViewport(viewport)
    ? MOBILE_CLAMP_PAD
    : DESKTOP_CLAMP_PAD;
}

export function scaleLimits(viewport: Viewport): { min: number; max: number } {
  const tight = isNarrowViewport(viewport) || isPortraitViewport(viewport);
  return {
    min: tight ? 0.38 : MIN_SCALE,
    max: MAX_SCALE,
  };
}

export function shouldSimplifyTiles(opts: {
  scale: number;
  visibleCount: number;
  viewportWidth: number;
}): boolean {
  const mobile = opts.viewportWidth <= NARROW_VIEWPORT_PX;
  return (
    opts.scale < (mobile ? 0.72 : 0.55) ||
    opts.visibleCount > (mobile ? 96 : 220)
  );
}

export function isHistoryGestureEdge(
  localX: number,
  width: number,
  edge = HISTORY_EDGE_PX,
): boolean {
  return localX < edge || localX > width - edge;
}

export function sheetShouldClose(dragY: number, threshold = SHEET_CLOSE_PX): boolean {
  return dragY >= threshold;
}

export const SWIPE_NAV_PX = 60;

/** Horizontal swipe direction: -1 (to previous, finger moved right), 1 (to next), or 0. */
export function swipeStep(dx: number, dy: number, threshold = SWIPE_NAV_PX): -1 | 0 | 1 {
  if (Math.abs(dx) < threshold || Math.abs(dx) < Math.abs(dy) * 1.5) return 0;
  return dx > 0 ? -1 : 1;
}
